import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import axiosApi from "../Utils/axiosApi";

const BillFormDialog = ({
  open,
  onClose,
  hostelList = [],
  onSuccess,
  initialHostelId = null,
  initialHostelerId = null,
  fixedSelection = false,
}) => {
  const [billingPeriodStartDate, setBillingPeriodStartDate] = useState("");
  const [billingPeriodEndDate, setBillingPeriodEndDate] = useState("");
  const [hostelerList, setHostelerList] = useState([]);

  const [selectedHostel, setSelectedHostel] = useState("");
  const [selectedHostelers, setSelectedHostelers] = useState([]);

  const [loadingHostelers, setLoadingHostelers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dateError, setDateError] = useState("");
  const [message, setMessage] = useState(null);

  // Fetch hostelers for the selected hostel.
  const fetchHostelers = async (hostelId) => {
    try {
      setLoadingHostelers(true);

      const response = await axiosApi.post("/hosteler/getHosteler", {
        hostelIds: [Number(hostelId)],
      });

      const data = Array.isArray(response.data) ? response.data : [];

      return data.map((h) => ({
        id: String(h.hostelerId ?? h.id ?? h[0]),
        hostlerName:
          h.hostlerName ||
          h.hostelerName ||
          `${h.hostlerFirstName || ""} ${h.hostlerLastName || ""}`.trim(),
      }));
    } catch (err) {
      console.error("Error fetching hostelers:", err);
      throw err;
    } finally {
      setLoadingHostelers(false);
    }
  };

  const generateBillService = async (payload) => {
    return axiosApi.post(
      "/hostelerPayment/generateHostelerBill",
      payload
    );
  };

  // Initialize the selected hostel when the dialog opens.
  useEffect(() => {
    if (!open) return;

    setMessage(null);
    setDateError("");

    if (initialHostelId != null) {
      setSelectedHostel(String(initialHostelId));
    } else {
      setSelectedHostel("");
      setSelectedHostelers([]);
      setHostelerList([]);
    }
  }, [open, initialHostelId]);

  // Load hostelers whenever the selected hostel changes.
  useEffect(() => {
    if (!open || !selectedHostel) {
      setHostelerList([]);
      setSelectedHostelers([]);
      return;
    }

    let cancelled = false;

    const loadHostelers = async () => {
      setHostelerList([]);
      setSelectedHostelers([]);

      try {
        const normalized = await fetchHostelers(selectedHostel);

        if (cancelled) return;

        setHostelerList(normalized);

        // Preselect the hosteler passed from HostelerGantt.
        if (initialHostelerId != null) {
          const matchingHosteler = normalized.find(
            (h) => h.id === String(initialHostelerId)
          );

          if (matchingHosteler) {
            setSelectedHostelers([matchingHosteler.id]);
          } else if (fixedSelection) {
            setMessage({
              type: "error",
              text: "The selected hosteler was not found for this hostel.",
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setHostelerList([]);
          setSelectedHostelers([]);
          setMessage({
            type: "error",
            text: "Failed to fetch hostelers.",
          });
        }
      }
    };

    loadHostelers();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    selectedHostel,
    initialHostelerId,
    fixedSelection,
  ]);

  const selectedHostelDetails = hostelList.find(
    (hostel) =>
      String(hostel.id ?? hostel.hostelId ?? hostel[0]) ===
      String(selectedHostel)
  );

  const selectedHostelName =
    selectedHostelDetails?.hostelName ??
    selectedHostelDetails?.name ??
    selectedHostelDetails?.[1] ??
    "";

  const selectedHosteler = hostelerList.find(
    (h) => h.id === String(selectedHostelers[0] ?? "")
  );

  const selectedHostelerName = selectedHosteler?.hostlerName ?? "";

  const validateBillingPeriod = () => {
    if (!billingPeriodStartDate || !billingPeriodEndDate) {
      setDateError("Please select both billing period dates.");
      return false;
    }

    const start = new Date(`${billingPeriodStartDate}T00:00:00`);
    const end = new Date(`${billingPeriodEndDate}T00:00:00`);

    if (end < start) {
      setDateError("End date cannot be before the start date.");
      return false;
    }

    const sameMonth =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth();

    if (!sameMonth) {
      setDateError("Start and End dates must be in the same month.");
      return false;
    }

    setDateError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateBillingPeriod()) return;

    if (loadingHostelers) return;

    if (!selectedHostel) {
      setMessage({
        type: "error",
        text: "Please select a hostel.",
      });
      return;
    }

    if (fixedSelection && !selectedHostelers.length) {
      setMessage({
        type: "error",
        text: "The selected hosteler could not be identified. Please check the hosteler ID.",
      });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const payload = {
        hostelId: selectedHostel ? Number(selectedHostel) : null,
        hostelerIds:
          selectedHostelers.length > 0
            ? selectedHosteler.map(Number)
            : null,
        billingPeriodStartDate,
        billingPeriodEndDate,
      };

      const response = await generateBillService(payload);

      const initiatedHostelers =
        response.data?.hostlerNamesWithPaymentInitiated || [];

      if (initiatedHostelers.length > 0) {
        setMessage({
          type: "warning",
          text: `Bill generated successfully. However, payment has already been initiated for: ${initiatedHostelers.join(
            ", "
          )}.`,
        });
      } else {
        setMessage({
          type: "success",
          text: "Bill generated successfully!",
        });
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error("Error generating bills:", err);

      setMessage({
        type: "error",
        text:
          err.response?.data?.message ||
          "Failed to generate bills.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setBillingPeriodStartDate("");
    setBillingPeriodEndDate("");
    setSelectedHostel("");
    setSelectedHostelers([]);
    setHostelerList([]);
    setDateError("");
    setMessage(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" ||
          reason === "escapeKeyDown"
        ) {
          return;
        }

        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Generate Bill</DialogTitle>

      <DialogContent>
        {message && (
          <Typography
            variant="body2"
            sx={{
              color:
                message.type === "success"
                  ? "green"
                  : message.type === "warning"
                  ? "orange"
                  : "red",
              mb: 2,
            }}
          >
            {message.text}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mt: 1,
          }}
        >
          {/* Gantt: show only the selected hostel.
              HostlerBill: preserve the original dropdown. */}
          {fixedSelection ? (
            <TextField
              label="Hostel"
              value={
                selectedHostelName ||
                (selectedHostel ? "Loading hostel..." : "")
              }
              fullWidth
              InputProps={{ readOnly: true }}
            />
          ) : (
            <FormControl fullWidth>
              <InputLabel id="bill-hostel-label">
                Hostel
              </InputLabel>

              <Select
                labelId="bill-hostel-label"
                label="Hostel"
                value={selectedHostel}
                onChange={(e) => setSelectedHostel(e.target.value)}
              >
                {hostelList.map((hostel) => {
                  const id = hostel.id ?? hostel.hostelId ?? hostel[0];
                  const name =
                    hostel.hostelName ?? hostel.name ?? hostel[1];

                  return (
                    <MenuItem key={id} value={String(id)}>
                      {name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {/* Gantt: show only the selected hosteler.
              HostlerBill: preserve the original multi-select. */}
          {fixedSelection ? (
            <TextField
              label="Hosteler"
              value={
                loadingHostelers
                  ? "Loading hosteler..."
                  : selectedHostelerName
              }
              fullWidth
              InputProps={{ readOnly: true }}
              disabled={loadingHostelers}
            />
          ) : (
            <FormControl
              fullWidth
              disabled={!selectedHostel || loadingHostelers}
            >
              <InputLabel id="bill-hostelers-label">
                Hostelers
              </InputLabel>

              <Select
                labelId="bill-hostelers-label"
                label="Hostelers"
                multiple
                value={selectedHostelers}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedHostelers(
                    typeof value === "string"
                      ? value.split(",")
                      : value
                  );
                }}
                renderValue={(selected) =>
                  selected
                    .map(
                      (id) =>
                        hostelerList.find(
                          (h) => h.id === String(id)
                        )?.hostlerName || id
                    )
                    .join(", ")
                }
              >
                {loadingHostelers ? (
                  <MenuItem disabled>
                    <CircularProgress size={18} sx={{ mr: 1 }} />
                    Loading hostelers...
                  </MenuItem>
                ) : (
                  hostelerList.map((h) => (
                    <MenuItem key={h.id} value={h.id}>
                      {h.hostlerName || h.id}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Billing Period Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={billingPeriodStartDate}
            onChange={(e) =>
              setBillingPeriodStartDate(e.target.value)
            }
            error={!!dateError}
          />

          <TextField
            label="Billing Period End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={billingPeriodEndDate}
            onChange={(e) =>
              setBillingPeriodEndDate(e.target.value)
            }
            error={!!dateError}
          />

          {dateError && (
            <Typography variant="body2" color="error">
              {dateError}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button onClick={handleClose} color="secondary">
              Close
            </Button>

            <Button
              variant="contained"
              disabled={
                submitting ||
                loadingHostelers ||
                !billingPeriodStartDate ||
                !billingPeriodEndDate ||
                !selectedHostel ||
                (fixedSelection && !selectedHosteler)
              }
              onClick={handleSubmit}
            >
              {submitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Generate"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BillFormDialog;